import { Head, Link, router } from "@inertiajs/react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { SingleImageUploader, ManagedImage } from "@/components/desk/SingleImageUploader";
import { Check, X } from "lucide-react";

interface Agency {
  id: number;
  name: string;
  description?: string | null;
  services?: string[] | string | null;
  working_hours?: WorkingHour[] | null;
  facebook_url?: string | null;
  instagram_url?: string | null;
  x_url?: string | null;
  website_url?: string | null;
  whatsapp_number?: string | null;
  phone_number?: string | null;
  address?: string | null;
  status?: string | null;
  trusted?: boolean;
  profile_image?: ManagedImage | null;
  cover_image?: ManagedImage | null;
  created_at: string;
  updated_at: string;
  user?: {
    id: number;
    name?: string | null;
    email?: string | null;
  } | null;
}

type WorkingHour = {
  day: string;
  opens_at: string;
  closes_at: string;
  closed: boolean;
};

const SERVICE_LABELS: Record<string, string> = {
  trading: "بيع العقارات",
  rental: "تأجير العقارات",
  legal_consulting: "استشارات قانونية",
  property_management: "إدارة ممتلكات",
};

const STATUS_LABELS: Record<string, string> = {
  active: "مفعلة",
  inactive: "غير فعالة",
  pending_verification: "قيد التحقق",
  suspended: "موقوفة",
};

interface Props {
  agency: Agency;
}

export default function Show({ agency }: Props) {
  const handleStatusChange = (status: "active" | "suspended") => {
    router.patch(
      `/desk/agencies/${agency.id}`,
      { agency: { status } },
      { preserveScroll: true, preserveState: true }
    );
  };

  const dayLabels: Record<string, string> = {
    sunday: "الأحد",
    monday: "الاثنين",
    tuesday: "الثلاثاء",
    wednesday: "الأربعاء",
    thursday: "الخميس",
    friday: "الجمعة",
    saturday: "السبت",
  };

  const workingHours = agency.working_hours || [];
  const servicesRaw = agency.services;
  const servicesList = (() => {
    if (Array.isArray(servicesRaw)) return servicesRaw;
    if (typeof servicesRaw === "string") {
      try {
        const parsed = JSON.parse(servicesRaw);
        if (Array.isArray(parsed)) return parsed as string[];
      } catch {
        // fall through
      }
      return servicesRaw
        .split(/[,،]/)
        .map((service: string) => service.trim())
        .filter(Boolean);
    }
    return [];
  })();

  const formatWorkingHour = (entry: WorkingHour) => {
    if (entry.closed) return "مغلق";
    if (entry.opens_at && entry.closes_at) {
      return `${entry.opens_at} - ${entry.closes_at}`;
    }
    return "غير محدد";
  };

  const statusLabel = agency.status ? STATUS_LABELS[agency.status] || agency.status : null;
  const infoItems = [
    {
      label: "الحالة",
      value: statusLabel,
    },
    {
      label: "وكالة موثوقة",
      value: agency.trusted ? "نعم" : "لا",
    },
    {
      label: "الخدمات",
      value:
        servicesList.length > 0
          ? servicesList.map((service: string) => SERVICE_LABELS[service] || service).join("، ")
          : null,
    },
    { label: "ساعات العمل", value: null },
    { label: "رقم الهاتف", value: agency.phone_number },
    { label: "رقم الواتساب", value: agency.whatsapp_number },
    {
      label: "رابط فيسبوك",
      value: agency.facebook_url ? (
        <a
          href={agency.facebook_url}
          target="_blank"
          rel="noreferrer"
          className="text-primary hover:underline"
        >
          {agency.facebook_url}
        </a>
      ) : undefined,
    },
    {
      label: "رابط إنستغرام",
      value: agency.instagram_url ? (
        <a
          href={agency.instagram_url}
          target="_blank"
          rel="noreferrer"
          className="text-primary hover:underline"
        >
          {agency.instagram_url}
        </a>
      ) : undefined,
    },
    {
      label: "رابط X",
      value: agency.x_url ? (
        <a
          href={agency.x_url}
          target="_blank"
          rel="noreferrer"
          className="text-primary hover:underline"
        >
          {agency.x_url}
        </a>
      ) : undefined,
    },
    {
      label: "الموقع الإلكتروني",
      value: agency.website_url ? (
        <a
          href={agency.website_url}
          target="_blank"
          rel="noreferrer"
          className="text-primary hover:underline"
        >
          {agency.website_url}
        </a>
      ) : undefined,
    },
  ];

  return (
    <>
      <Head title={`تفاصيل الوكالة ${agency.name}`} />

      <Card>
        <CardHeader className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle>{agency.name}</CardTitle>
            <CardDescription>تفاصيل الوكالة والمعلومات الأساسية</CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" asChild>
              <Link href="/desk/agencies">عودة للقائمة</Link>
            </Button>
            <Button asChild>
              <Link href={`/desk/agencies/${agency.id}/edit`}>تعديل الوكالة</Link>
            </Button>
            {agency.status === "pending_verification" && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleStatusChange("active")}
                  className="text-emerald-600"
                >
                  <Check className="h-4 w-4" />
                  <span className="sr-only">اعتماد</span>
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleStatusChange("suspended")}
                  className="text-rose-600"
                >
                  <X className="h-4 w-4" />
                  <span className="sr-only">رفض</span>
                </Button>
              </>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2">
            <SingleImageUploader
              title="صورة الملف الشخصي"
              description="حمّل صورة الملف الشخصي للوكالة"
              image={agency.profile_image}
              uploadPath={`/desk/agencies/${agency.id}/images`}
              deletePath={(id) => `/desk/agencies/${agency.id}/images/${id}`}
              kind="profile"
            />
            <SingleImageUploader
              title="صورة الغلاف"
              description="حمّل صورة الغلاف للوكالة"
              image={agency.cover_image}
              uploadPath={`/desk/agencies/${agency.id}/images`}
              deletePath={(id) => `/desk/agencies/${agency.id}/images/${id}`}
              kind="cover"
            />
          </div>

          {agency.description && (
            <div>
              <h3 className="text-sm font-semibold text-muted-foreground">الوصف</h3>
              <p className="mt-2 text-sm leading-6 text-foreground">{agency.description}</p>
            </div>
          )}

          {agency.user && (
            <div className="rounded-lg border bg-muted/40 p-4">
              <p className="text-xs font-medium text-muted-foreground">المستخدم</p>
              <div className="mt-2 space-y-1 text-sm text-foreground">
                <p className="font-semibold">{agency.user.name || agency.user.email || "—"}</p>
                {agency.user.email && (
                  <p className="text-xs text-muted-foreground">{agency.user.email}</p>
                )}
              </div>
            </div>
          )}

          {agency.address && (
            <div className="rounded-lg border bg-muted/40 p-4">
              <p className="text-xs font-medium text-muted-foreground">العنوان</p>
              <p className="mt-2 text-sm text-foreground whitespace-pre-line">{agency.address}</p>
            </div>
          )}

          {workingHours.length > 0 && (
            <div className="rounded-lg border bg-muted/40 p-4">
              <h3 className="text-sm font-semibold text-muted-foreground">ساعات العمل</h3>
              <div className="mt-3 grid gap-2 md:grid-cols-2">
                {workingHours.map((entry) => (
                  <div
                    key={entry.day}
                    className="flex items-center justify-between rounded-md bg-background px-3 py-2"
                  >
                    <span className="text-sm font-medium text-foreground">
                      {dayLabels[entry.day] || entry.day}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {formatWorkingHour(entry)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid gap-4 md:grid-cols-2">
            {infoItems.map((item) => {
              if (!item.value) {
                return null;
              }

              return (
                <div key={item.label} className="rounded-lg border bg-muted/40 p-4">
                  <p className="text-xs font-medium text-muted-foreground">{item.label}</p>
                  <div className="mt-2 text-sm text-foreground">{item.value}</div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </>
  );
}
